import { useEffect, useRef } from "react";
import { defineStepper } from "@/components/stepper";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import Signature, { type SignatureRef } from "@uiw/react-signature";

import { db } from "@/stores/indexed-db";
import { useUserValue } from "@/stores/user-store";
import { useGeolocation, useMediaQuery } from "@uidotdev/usehooks";

import {
    personalInformationSchema,
    meansOfIdentificationSchema,
    contactInformationSchema,
    residentialInformationSchema,
    clusterInformationSchema,
    farmInformationSchema,
    packageInformationSchema,
    type PersonalInformation,
    type MeansOfIdentification,
    type ContactInformation,
    type ResidentialInformation,
    type ClusterInformation,
    type FarmInformation,
    type PackageInformation
} from "@/doctypes/aa_farmer.zod";
import * as ENUMS from "@/models/enums";

const PersonalInfoForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<PersonalInformation>();

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("first_name").name}>First Name</Label>
                <Input id={register("first_name").name} {...register("first_name")} className="block w-full rounded-md border p-2" />
                {errors.first_name && <p className="text-sm text-destructive">{errors.first_name.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("middle_name").name}>Middle Name</Label>
                <Input id={register("middle_name").name} {...register("middle_name")} className="block w-full rounded-md border p-2" />
                {errors.middle_name && <p className="text-sm text-destructive">{errors.middle_name.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("last_name").name}>Last Name</Label>
                <Input id={register("last_name").name} {...register("last_name")} className="block w-full rounded-md border p-2" />
                {errors.last_name && <p className="text-sm text-destructive">{errors.last_name.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("gender").name}>Gender</Label>
                <Select onValueChange={(value: typeof ENUMS.GENDERS[number]) => setValue("gender", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Gender" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.GENDERS.map((gender) => (
                            <SelectItem key={gender} value={gender}>
                                {gender}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.gender && <p className="text-sm text-destructive">{errors.gender.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("date_of_birth").name}>Date of Birth</Label>
                <Input
                    id={register("date_of_birth").name}
                    {...register("date_of_birth")}
                    type="date"
                    className="block w-full rounded-md border p-2"
                />
                {errors.date_of_birth && <p className="text-sm text-destructive">{errors.date_of_birth.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("passport_photo").name}>Passport Photo</Label>
                <Input
                    id={register("passport_photo").name}
                    {...register("passport_photo")}
                    type="file"
                    className="block w-full rounded-md border px-2"
                />
                {errors.passport_photo && <p className="text-sm text-destructive">{errors.passport_photo.message}</p>}
            </div>
        </div>
    );
}

const ContactInfoForm = () => {
    const { register, formState: { errors } } = useFormContext<ContactInformation>();

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("phone_number").name}>Phone Number</Label>
                <Input id={register("phone_number").name} {...register("phone_number")} className="block w-full rounded-md border p-2" />
                {errors.phone_number && <p className="text-sm text-destructive">{errors.phone_number.message}</p>}
            </div>
        </div>
    );
}

// Identification form component
const IdentificationForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<MeansOfIdentification>();

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("id_type").name}>ID Type</Label>
                <Select onValueChange={(value: typeof ENUMS.ID_TYPES[number]) => setValue("id_type", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select ID Type" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.ID_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                                {type}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.id_type && <p className="text-sm text-destructive">{errors.id_type.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("id_number").name}>ID Number</Label>
                <Input id={register("id_number").name} {...register("id_number")} className="block w-full rounded-md border p-2" />
                {errors.id_number && <p className="text-sm text-destructive">{errors.id_number.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("id_photo").name}>ID Photo</Label>
                <Input id={register("id_photo").name} {...register("id_photo")} type="file" className="block w-full rounded-md border px-2" />
                {errors.id_photo && <p className="text-sm text-destructive">{errors.id_photo.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("bvn").name}>BVN</Label>
                <Input id={register("bvn").name} {...register("bvn")} className="block w-full rounded-md border p-2" />
                {errors.bvn && <p className="text-sm text-destructive">{errors.bvn.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("bvn_photo").name}>BVN Photo</Label>
                <Input id={register("bvn_photo").name} {...register("bvn_photo")} type="file" className="block w-full rounded-md border px-2" />
                {errors.bvn_photo && <p className="text-sm text-destructive">{errors.bvn_photo.message}</p>}
            </div>
        </div>
    );
}

// Residential information form component
const ResidentialForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<ResidentialInformation>();

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("state").name}>State</Label>
                <Select onValueChange={(value: typeof ENUMS.STATES[number]) => setValue("state", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.STATES.map((state) => (
                            <SelectItem key={state} value={state}>
                                {state}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.state && <p className="text-sm text-destructive">{errors.state.message}</p>}
            </div>
        </div>
    );
}

// Cluster information form component
const ClusterForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<ClusterInformation>();

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("cluster_name").name}>Cluster Name</Label>
                <Select onValueChange={(value: typeof ENUMS.CLUSTERS[number]) => setValue("cluster_name", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Cluster" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.CLUSTERS.map((cluster) => (
                            <SelectItem key={cluster} value={cluster}>
                                {cluster}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.cluster_name && <p className="text-sm text-destructive">{errors.cluster_name.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("cooperative_name").name}>Cooperative Name</Label>
                <Input id={register("cooperative_name").name} {...register("cooperative_name")} className="block w-full rounded-md border p-2" />
                {errors.cooperative_name && <p className="text-sm text-destructive">{errors.cooperative_name.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("cooperative_role").name}>Cooperative Role</Label>
                <Select onValueChange={(value: typeof ENUMS.COOPERATIVE_ROLES[number]) => setValue("cooperative_role", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Role" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.COOPERATIVE_ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                                {role}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.cooperative_role && <p className="text-sm text-destructive">{errors.cooperative_role.message}</p>}
            </div>
        </div>
    );
}

// Farm information form component
const FarmForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<FarmInformation>();
    const location = useGeolocation({
        enableHighAccuracy: true,
        timeout: 5000
    });

    useEffect(() => {
        if (location) {
            setValue("longitude", Number(location.longitude?.toPrecision(6)));
            setValue("latitude", Number(location.latitude?.toPrecision(6)));
        }
    }, [location]);

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("commodity").name}>Commodity</Label>
                <Select onValueChange={(value: typeof ENUMS.COMMODITIES[number]) => setValue("commodity", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Commodity" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.COMMODITIES.map((option) => (
                            <SelectItem key={option} value={option}>
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.commodity && <p className="text-sm text-destructive">{errors.commodity.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("size_in_hectares").name}>Size in Hectares</Label>
                <Input
                    id={register("size_in_hectares").name}
                    {...register("size_in_hectares", { valueAsNumber: true })}
                    type="number"
                    className="block w-full rounded-md border p-2"
                />
                {errors.size_in_hectares && <p className="text-sm text-destructive">{errors.size_in_hectares.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("previously_used_for").name}>Previously Used For</Label>
                <Select onValueChange={(value: typeof ENUMS.PREVIOUSLY_USED_FOR[number]) => setValue("previously_used_for", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Previous Use" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.PREVIOUSLY_USED_FOR.map((option) => (
                            <SelectItem key={option} value={option}>
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.previously_used_for && <p className="text-sm text-destructive">{errors.previously_used_for.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("longitude").name}>Longitude</Label>
                <Input
                    id={register("longitude").name}
                    {...register("longitude", { valueAsNumber: true })}
                    type="number"
                    step="0.000001"
                    className="block w-full rounded-md border p-2"
                />
                {errors.longitude && <p className="text-sm text-destructive">{errors.longitude.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("latitude").name}>Latitude</Label>
                <Input
                    id={register("latitude").name}
                    {...register("latitude", { valueAsNumber: true })}
                    type="number"
                    step="0.000001"
                    className="block w-full rounded-md border p-2"
                />
                {errors.latitude && <p className="text-sm text-destructive">{errors.latitude.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("number_of_hectares_financed").name}>Number of Hectares Financed</Label>
                <Input
                    id={register("number_of_hectares_financed").name}
                    {...register("number_of_hectares_financed", { valueAsNumber: true })}
                    type="number"
                    className="block w-full rounded-md border p-2"
                />
                {errors.number_of_hectares_financed && <p className="text-sm text-destructive">{errors.number_of_hectares_financed.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("last_year_harvest_yield").name}>Last Year Harvest Yield</Label>
                <Select onValueChange={(value: typeof ENUMS.COMMODITIES[number]) => setValue("last_year_harvest_yield", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Harvest Type" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.COMMODITIES.map((option) => (
                            <SelectItem key={option} value={option}>
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.last_year_harvest_yield && <p className="text-sm text-destructive">{errors.last_year_harvest_yield.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("number_of_bags_harvested").name}>Number of Bags Harvested</Label>
                <Input
                    id={register("number_of_bags_harvested").name}
                    {...register("number_of_bags_harvested", { valueAsNumber: true })}
                    type="number"
                    className="block w-full rounded-md border p-2"
                />
                {errors.number_of_bags_harvested && <p className="text-sm text-destructive">{errors.number_of_bags_harvested.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("cost_per_bag").name}>Cost per Bag</Label>
                <Input
                    id={register("cost_per_bag").name}
                    {...register("cost_per_bag", { valueAsNumber: true })}
                    type="number"
                    className="block w-full rounded-md border p-2"
                />
                {errors.cost_per_bag && <p className="text-sm text-destructive">{errors.cost_per_bag.message}</p>}
            </div>
        </div>
    );
}

// Package information form component
const PackageForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<PackageInformation>();
    const signatureRef = useRef<SignatureRef>(null);

    const handleSaveSignature = () => {
        if (signatureRef.current?.svg) {
            const svgData = signatureRef.current.svg;
            // Convert SVG to a string
            const svgString = new XMLSerializer().serializeToString(svgData);

            // Create a temporary SVG image to draw on canvas
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Set dimensions for the image
            canvas.width = 500;
            canvas.height = 200;

            // Create a data URL representing the SVG
            const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(svgBlob);

            // Use a promise to handle the asynchronous loading of the image
            img.onload = () => {
                // Draw the image on the canvas
                if (ctx) {
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    // Convert canvas to PNG
                    const pngDataUrl = canvas.toDataURL('image/png');

                    // Convert data URL to blob
                    const byteString = atob(pngDataUrl.split(',')[1]);
                    const mimeString = pngDataUrl.split(',')[0].split(':')[1].split(';')[0];
                    const ab = new ArrayBuffer(byteString.length);
                    const ia = new Uint8Array(ab);

                    for (let i = 0; i < byteString.length; i++) {
                        ia[i] = byteString.charCodeAt(i);
                    }

                    const pngBlob = new Blob([ab], { type: mimeString });

                    // Create a File object
                    const file = new File([pngBlob], 'signature.png', { type: 'image/png' });

                    // Create a DataTransfer to get a FileList
                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(file);

                    // Set the FileList to the signature field
                    setValue('signature', dataTransfer.files);

                    // Clean up
                    URL.revokeObjectURL(url);

                    toast.info("Signature saved as PNG");
                }
            };

            // Set the image source to the object URL
            img.src = url;
        }
    };

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("input_package").name}>Input Package</Label>
                <Select onValueChange={(value: typeof ENUMS.INPUT_PACKAGES[number]) => setValue("input_package", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Package" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.INPUT_PACKAGES.map((option) => (
                            <SelectItem key={option} value={option}>
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.input_package && <p className="text-sm text-destructive">{errors.input_package.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("number_of_hectares_subscribed").name}>Number of Hectares Subscribed</Label>
                <Input
                    id={register("number_of_hectares_subscribed").name}
                    {...register("number_of_hectares_subscribed", { valueAsNumber: true })}
                    type="number"
                    className="block w-full rounded-md border p-2"
                />
                {errors.number_of_hectares_subscribed && <p className="text-sm text-destructive">{errors.number_of_hectares_subscribed.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("signature").name}>Signature</Label>
                <Signature
                    id={register("signature").name}
                    {...register("signature")}
                    ref={signatureRef}
                    options={{
                        size: 6,
                        simulatePressure: true,
                        smoothing: 0.4,
                        thinning: 0.7,
                    }}
                    onChange={(e) => console.log(e)}
                    className="rounded-md border p-2" />
                <div className="flex justify-start gap-2">
                    <Button variant="outline" onClick={() => signatureRef.current?.clear()}>Clear</Button>
                    <Button onClick={() => handleSaveSignature()}>Save</Button>
                </div>
                {/* {errors.signature && <p className="text-sm text-destructive">{errors.signature.message}</p>} */}
            </div>
        </div>
    );
}

const { Stepper, useStepper } = defineStepper(
    {
        id: "contact_information",
        title: "Contact",
        schema: contactInformationSchema,
        component: ContactInfoForm,
    },
    {
        id: "personal_information",
        title: "Personal Information",
        schema: personalInformationSchema,
        component: PersonalInfoForm,
    },
    {
        id: "means_of_identification",
        title: "Identification",
        schema: meansOfIdentificationSchema,
        component: IdentificationForm,
    },
    {
        id: "residential_information",
        title: "Residence",
        schema: residentialInformationSchema,
        component: ResidentialForm,
    },
    {
        id: "cluster_information",
        title: "Cluster",
        schema: clusterInformationSchema,
        component: ClusterForm,
    },
    {
        id: "farm_information",
        title: "Farm",
        schema: farmInformationSchema,
        component: FarmForm,
    },
    {
        id: "package_information",
        title: "Package",
        schema: packageInformationSchema,
        component: PackageForm,
    }
)

export const FarmerOnboardingForm = () => {
    return (
        <Stepper.Provider variant="vertical" tracking>
            <FormComponent />
        </Stepper.Provider>
    );
}

const FormComponent = () => {
    const methods = useStepper();
    const user = useUserValue("name");
    const isMobile = useMediaQuery("(max-width: 768px)");

    const form = useForm<
        z.infer<typeof personalInformationSchema> |
        z.infer<typeof meansOfIdentificationSchema> |
        z.infer<typeof contactInformationSchema> |
        z.infer<typeof residentialInformationSchema> |
        z.infer<typeof clusterInformationSchema> |
        z.infer<typeof farmInformationSchema> |
        z.infer<typeof packageInformationSchema>
    >({
        mode: "onBlur",
        resolver: zodResolver(methods.current.schema as any)
    });

    const onSubmit = async (data: z.infer<typeof methods.current.schema>) => {
        const farmer_id = form.getValues("phone_number");

        if (methods.isFirst) {
            const existingFarmer = await db.farmers.get(farmer_id);
            if (existingFarmer) {
                toast.error("Farmer already exists");
                return;
            }

            db.farmers.add({
                ...data,
                phone_number: farmer_id,
                status: "pending",
                creation: new Date().toISOString(),
                modified: new Date().toISOString(),
            })
        }

        await db.farmers.update(farmer_id, {
            ...data,
            modified: new Date().toISOString(),
        })

        if (!methods.isLast) {
            methods.next();
            return;
        }

        await db.farmers.update(farmer_id, {
            status: "unsynced",
            modified: new Date().toISOString(),
        });

        form.reset();
        methods.reset();
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <Stepper.Navigation>
                    {methods.all.map((step) => (
                        <Stepper.Step
                            key={step.id}
                            of={step.id}
                            type="button"
                            onClick={async () => {
                                const valid = await form.trigger();
                                if (!valid) return;
                                methods.goTo(step.id);
                            }}
                        >
                            <Stepper.Title>{step.title}</Stepper.Title>
                            {methods.when(step.id, () =>
                                <Stepper.Panel>
                                    {methods.switch({
                                        personal_information: () => <PersonalInfoForm />,
                                        means_of_identification: () => <IdentificationForm />,
                                        contact_information: () => <ContactInfoForm />,
                                        residential_information: () => <ResidentialForm />,
                                        cluster_information: () => <ClusterForm />,
                                        farm_information: () => <FarmForm />,
                                        package_information: () => <PackageForm />
                                    })}
                                    <Stepper.Controls className="mt-4">
                                        <Button variant="secondary" onClick={methods.prev} disabled={methods.isFirst}>Previous</Button>
                                        <Button
                                            type="submit"
                                            onClick={() => {
                                                form.trigger();
                                            }}>
                                            {methods.isLast ? "Submit" : "Next"}
                                        </Button>
                                    </Stepper.Controls>
                                </Stepper.Panel>
                            )}
                        </Stepper.Step>
                    ))}
                </Stepper.Navigation>
            </form>
        </Form>
    );
}